<template>
  <div
    class="app"
    :class="{ 'landing-mode': isLandingPage }"
    :data-theme="isDarkMode ? 'dark' : 'light'"
  >
    <navigation-bar v-if="!hideHeader" />
    <main
      class="main-content"
      :class="{ 'with-header': !hideHeader }"
    >
      <router-view v-slot="{ Component, route: viewRoute }">
        <transition
          name="page"
          mode="out-in"
        >
          <component
            :is="Component"
            :key="viewRoute.path"
          />
        </transition>
      </router-view>
    </main>
    <app-footer v-if="!isLandingPage" />

    <AppleRelayEmailModal v-model="showAppleRelayModal" />
  </div>
</template>

<script setup>
import { computed, onMounted, watch, ref } from 'vue';
import { useRoute } from 'vue-router';
import { setLanguage } from '@/i18n';
import NavigationBar from '@/components/layout/NavigationBar.vue';
import AppFooter from '@/components/layout/AppFooter.vue';
import AppleRelayEmailModal from '@/components/ui/AppleRelayEmailModal.vue';
import { useThemeStore } from '@/store/theme';
import { useAuthStore } from '@/store/auth';

// Theme handling
const themeStore = useThemeStore();
const authStore = useAuthStore();
const isDarkMode = computed(() => themeStore.isDark);

// Determine if current page is landing page
const route = useRoute();
const hideHeader = computed(() => route.meta?.noHeader === true);
const isLandingPage = ref(false);
const showAppleRelayModal = ref(false);

if (localStorage.getItem('utm_campaign') === 'undefined') {
  localStorage.removeItem('utm_campaign');
}

if (localStorage.getItem('utm_medium') === 'undefined') {
  localStorage.removeItem('utm_medium');
}

if (localStorage.getItem('utm_source') === 'undefined') {
  localStorage.removeItem('utm_source');
}

// Watch route changes for analytics and page title updates
watch(() => route.path, () => {
  // Update page title based on route
  const routeName = route.name;
  document.title = routeName
    ? `${routeName.charAt(0).toUpperCase() + routeName.slice(1)} | Indiefy`
    : 'Indiefy - Music Distribution Platform and Promotion Tools';

  // We could add analytics tracking here
});

// Watch for query params and store referral if present
watch(
  () => route.query,
  (newQuery) => {
    if (newQuery.ref) {
      localStorage.setItem('referral', newQuery.ref);
    }

    if (newQuery.from) {
      localStorage.setItem('utm_campaign', newQuery.from);
    }

    if (newQuery.utm_campaign) {
      localStorage.setItem('utm_campaign', newQuery.utm_campaign);
    }

    if (newQuery.utm_medium) {
      localStorage.setItem('utm_medium', newQuery.utm_medium);
    }

    if (newQuery.utm_source) {
      localStorage.setItem('utm_source', newQuery.utm_source);
    }

    if (newQuery.lang) {
      setLanguage(newQuery.lang);
    }
  },
  { immediate: true },
);

// Watch for auth changes to enforce theme restrictions
watch(
  () => authStore.currentUser,
  () => {
    // Enforce light mode for non-PRO users when auth state changes
    themeStore.enforceLightModeForBasicUsers();
  },
  { immediate: false },
);

// Check for Apple private relay email after user is loaded
watch(
  () => authStore.currentUser,
  (user) => {

    if (
      user?.email?.includes('privaterelay.appleid.com')
    ) {
      showAppleRelayModal.value = true;
    }
  },
  { immediate: true },
);

// Initial theme setup
onMounted(() => {
  // Only use saved theme from localStorage (ignore system preferences)
  const savedTheme = localStorage.getItem('indiefy-theme');

  if (savedTheme === 'dark') {
    // Will check PRO status inside setDarkMode
    themeStore.setDarkMode(true);
  }

  // Enforce light mode for non-PRO users after initial load
  setTimeout(() => {
    themeStore.enforceLightModeForBasicUsers();
  }, 100);

  window.smartlook && window.smartlook('record', {
    forms: true,
    numbers: true,
    emails: false,
    ips: true,
  });

});
</script>

<style>
.page-enter-active,
.page-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.body--dark .label {
  color: white;
}

.pro-artists-carousel .carousel__viewport {
  padding-top: 1rem;
  padding-bottom: 1rem;
  overflow: visible !important;
}

@media (max-width: 600px) {
  .pro-artists-carousel .carousel__slide .artist-card {
    border-radius: 0 !important;
  }
}
</style>